// PricingModal — 下载配额超额时弹出，引导用户订阅 Creator 计划
import { useState, useEffect } from "react";
import { t } from "../i18n";

export default function PricingModal({ lang, onClose }) {
  const [quota, setQuota] = useState(null);
  const [_loading, setLoading] = useState(true);
  const [subscribeLoading, setSubscribeLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("fm_token");
    if (!token) {
      setLoading(false);
      return;
    }
    fetch("/api/user/download-quota", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        setQuota(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const isSubscribed = quota?.is_creator || quota?.plan === "creator";

  async function handleSubscribe(period) {
    const token = localStorage.getItem("fm_token");
    if (!token) return;
    setSubscribeLoading(true);
    setError("");
    try {
      const res = await fetch("/api/stripe/create-checkout-session", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          price_type: period,
          success_url: `${window.location.origin}/?payment=success`,
          cancel_url: window.location.href,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to create checkout session");
      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (e) {
      setError(e.message);
      setSubscribeLoading(false);
    }
  }

  async function handleManageSubscription() {
    const token = localStorage.getItem("fm_token");
    if (!token) return;
    setPortalLoading(true);
    setError("");
    try {
      const res = await fetch("/api/stripe/portal", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to open portal");
      window.location.href = data.url;
    } catch (e) {
      setError(e.message);
      setPortalLoading(false);
    }
  }

  async function handleBuyCredits() {
    const token = localStorage.getItem("fm_token");
    if (!token) return;
    setSubscribeLoading(true);
    setError("");
    try {
      const res = await fetch("/api/stripe/create-checkout-session", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          price_type: "one_time",
          success_url: `${window.location.origin}/?payment=success`,
          cancel_url: window.location.href,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to create checkout session");
      window.location.href = data.url;
    } catch (e) {
      setError(e.message);
      setSubscribeLoading(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        {/* Icon */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto bg-amber-100 rounded-2xl flex items-center justify-center mb-4">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">{t(lang, 'upgradeTitle')}</h2>
          <p className="text-sm text-gray-500">{t(lang, 'upgradeDesc')}</p>
        </div>

        {/* Quota status */}
        {quota && !quota.is_creator && (
          <div className="text-center text-sm text-gray-500 mb-6">
            {t(lang, 'quotaUsed')}: <span className="font-medium text-gray-900">{quota.downloads_used || 0}</span>
            {" / "}
            {t(lang, 'quotaRemaining')}: <span className="font-medium text-gray-900">{Math.max(0, (quota.downloads_limit || 20) - (quota.downloads_used || 0))}</span>
            {" / "}
            <span className="text-gray-400">{t(lang, 'perMonth')}</span>
            {quota.downloads_remaining > Math.max(0, (quota.downloads_limit || 20) - (quota.downloads_used || 0)) && (
              <span className="block mt-1 text-xs text-amber-600">
                +{quota.downloads_remaining - Math.max(0, (quota.downloads_limit || 20) - (quota.downloads_used || 0))} {t(lang, 'bonusDownloads')}
              </span>
            )}
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="text-center text-sm text-red-500 mb-4 bg-red-50 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="space-y-3">
          {/* Creator subscription — monthly & yearly */}
        {isSubscribed ? (
          <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-200 mb-3">
            <div className="flex items-center justify-between mb-2">
              <div>
                <span className="font-bold text-indigo-700">{t(lang, 'creatorTier')}</span>
              </div>
              <span className="text-lg font-bold text-indigo-700">{t(lang, 'planActive')}</span>
            </div>
            <p className="text-xs text-indigo-600 mb-3">{t(lang, 'alreadySubscribed')}</p>
            <button
              onClick={handleManageSubscription}
              disabled={portalLoading}
              className="w-full py-2.5 bg-white border border-indigo-300 text-indigo-700 hover:bg-indigo-100 font-medium rounded-lg transition text-sm disabled:opacity-50"
            >
              {portalLoading ? t(lang, 'redirectingToStripe') : t(lang, 'manageSubscription')}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 mb-3">
            {/* Monthly */}
            <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200 flex flex-col">
              <div className="flex items-center gap-1 mb-1">
                <span className="font-bold text-indigo-700 text-sm">{t(lang, 'monthly')}</span>
              </div>
              <div className="mb-2">
                <span className="text-lg font-bold text-indigo-700">$3</span>
                <span className="text-xs text-indigo-500">{t(lang, 'perMonth')}</span>
              </div>
              <p className="text-xs text-indigo-600 mb-3 flex-grow">{t(lang, 'subscribeForUnlimited')}</p>
              <button
                onClick={() => handleSubscribe("monthly")}
                disabled={subscribeLoading}
                className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition text-xs disabled:opacity-50"
              >
                {subscribeLoading ? t(lang, 'redirectingToStripe') : t(lang, 'subscribeNow')}
              </button>
            </div>

            {/* Yearly — Best Value */}
            <div className="bg-amber-50 rounded-xl p-4 border border-amber-300 flex flex-col relative">
              <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
                <span className="bg-amber-400 text-white text-xs font-bold px-2.5 py-0.5 rounded-full shadow-sm">{t(lang, 'bestValue')}</span>
              </div>
              <div className="flex items-center gap-1 mb-1 mt-1">
                <span className="font-bold text-amber-700 text-sm">{t(lang, 'yearly')}</span>
              </div>
              <div className="mb-2">
                <span className="text-lg font-bold text-amber-700">$30</span>
                <span className="text-xs text-amber-500">{t(lang, 'perYear')}</span>
              </div>
              <p className="text-xs text-amber-600 mb-3 flex-grow">{t(lang, 'save2Months')}</p>
              <button
                onClick={() => handleSubscribe("yearly")}
                disabled={subscribeLoading}
                className="w-full py-2 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-lg transition text-xs disabled:opacity-50"
              >
                {subscribeLoading ? t(lang, 'redirectingToStripe') : t(lang, 'subscribeNow')}
              </button>
            </div>
          </div>
        )}

          {/* Pay-per-use: $1 for 50 credits */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-gray-800">{t(lang, 'payPerUse')}</span>
              <span className="text-lg font-bold text-gray-900">$1</span>
            </div>
            <p className="text-xs text-gray-500 mb-3">{t(lang, 'creditsDesc')}</p>
            <button
              onClick={handleBuyCredits}
              disabled={subscribeLoading || portalLoading}
              className="w-full py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 font-medium rounded-lg transition text-sm disabled:opacity-50"
            >
              {subscribeLoading || portalLoading ? t(lang, 'redirectingToStripe') : t(lang, 'buyCredits')}
            </button>
          </div>
        </div>

        {/* Still hesitating */}
        <div className="text-center mt-6">
          <span className="text-xs text-gray-400">{t(lang, 'stillHesitate')} </span>
          <button onClick={onClose} className="text-xs text-indigo-500 hover:text-indigo-600 font-medium">
            {t(lang, 'stillHesitateLink')}
          </button>
          <span className="text-xs text-gray-400"> {t(lang, 'stillHesitateSuffix')}</span>
        </div>
      </div>
    </div>
  );
}
